import React, { useState, useEffect, useMemo } from 'react';
import { SchedulingService, type AvailabilityDay, type ScheduledSession } from '../../services/schedulingService';
import { AccountService } from '../../services/accountService';
import type { GuideDetails } from '../../services/matchingService';
import { getSupabaseClient } from '../../lib/supabase';
import { formatTimeForDisplay, getBrowserTimezone, formatUtcInTimezone } from '../../utils/dateUtils';
import { seekerDataService } from '../../services/seekerDataService';
import { isTimeTooSoon, convertUtcToSeekerTime } from '../../utils/schedulingUtils';
import { trackCapiEvent } from '../../utils/trackCapiEvent';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import { PasswordInput } from '@ui';

const canFitSession = (
  timeSlots: Array<{ time: string; available: boolean }>,
  startTime: string,
  durationMinutes: number
): boolean => {
  // Find the index of the start time
  const startIndex = timeSlots.findIndex(slot => slot.time === startTime);
  if (startIndex === -1 || !timeSlots[startIndex].available) {
    return false;
  }

  // Calculate how many 15-minute slots we need (assuming slots are 15 minutes apart)
  const slotsNeeded = Math.ceil(durationMinutes / 15);

  // Check if we have enough consecutive available slots
  for (let i = 0; i < slotsNeeded; i++) {
    const slotIndex = startIndex + i;
    if (slotIndex >= timeSlots.length || !timeSlots[slotIndex].available) {
      return false;
    }
  }

  return true;
};

/**
 * Format timezone name for display (e.g., "America/New_York" -> "ET")
 * Uses shortGeneric to avoid EST/EDT confusion (GCS-191).
 */
const formatTimezoneForDisplay = (timezone: string | null): string => {
  if (!timezone) return 'UTC';

  try {
    const now = new Date();
    // Try shortGeneric first (returns "ET" instead of "EST"/"EDT")
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortGeneric'
      });
      const parts = formatter.formatToParts(now);
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      if (tzPart) return tzPart.value;
    } catch { /* shortGeneric not supported, fall through */ }

    // Fallback to short
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    if (tzPart) return tzPart.value;

    return timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
  } catch (error) {
    console.error('Error formatting timezone:', error);
    return timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
  }
};

type ConsultationSchedulerProps = {
    guide: GuideDetails;
    password?: string;
    onBack?: () => void;
    onBookingComplete?: (session: ScheduledSession) => void;
    accountAlreadyCreated?: boolean;
};

export const ConsultationScheduler: React.FC<ConsultationSchedulerProps> = ({
    guide,
    password: providedPassword,
    onBack,
    onBookingComplete,
    accountAlreadyCreated = false,
}) => {
    const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [isLoadingAvailability, setIsLoadingAvailability] = useState(true);
    const [selectedTime, setSelectedTime] = useState<string>('');
    const [selectedStartUtc, setSelectedStartUtc] = useState<string>('');
    const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
    const [password, setPassword] = useState('');
    const [isBooking, setIsBooking] = useState(false);
    const [bookingError, setBookingError] = useState<string>('');
    const [hasCalendarIntegration, setHasCalendarIntegration] = useState(false);
    const [seekerTimezone, setSeekerTimezone] = useState<string | null>(null);
    const [seekerPostcode, setSeekerPostcode] = useState<string | null>(null);
    const [guideTimezone, setGuideTimezone] = useState<string | null>(null);
    const supabase = getSupabaseClient();

    // Load seeker timezone and postcode on mount
    useEffect(() => {
        const loadSeekerInfo = async () => {
            const [tz, postcode] = await Promise.all([
                seekerDataService.getSeekerTimezone(),
                seekerDataService.getSeekerPostcode(),
            ]);
            setSeekerTimezone(tz || getBrowserTimezone());
            setSeekerPostcode(postcode);
        };
        loadSeekerInfo();
    }, []);

    useEffect(() => {
        if (guide?.guide_id) {
            setIsLoadingAvailability(true);

            // Try to fetch with calendar integration info
            const fetchAvailability = async () => {
                try {
                    const env = (window as any).__ENV__;
                    const baseUrl = env?.SUPABASE_URL;

                    if (baseUrl) {
                        const today = new Date();
                        const toLocalDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        const startDate = toLocalDate(today);
                        const endDate = toLocalDate(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000));

                        const response = await fetch(
                            `${baseUrl}/functions/v1/guide-availability?guide_id=${guide.guide_id}&start_date=${startDate}&end_date=${endDate}`
                        );

                        if (response.ok) {
                            const data = await response.json();
                            // Transform to match expected format
                            const transformedAvailability = data.availability.map((day: any) => ({
                                ...day,
                                dayOfWeek: new Date(day.date).toLocaleDateString('en-US', { weekday: 'long' }),
                            }));
                            setAvailability(transformedAvailability);
                            setHasCalendarIntegration(data.has_calendar_integration || false);
                            setGuideTimezone(data.guide_timezone || null);
                            return;
                        }
                    }

                    // Fallback to old method
                    const data = await SchedulingService.getGuideAvailability(guide.guide_id);
                    setAvailability(data);
                    setHasCalendarIntegration(false);
                } catch (error) {
                    console.error('Error loading availability:', error);
                    setBookingError('Failed to load availability. Please try again.');
                } finally {
                    setIsLoadingAvailability(false);
                }
            };

            fetchAvailability();
        }
    }, [guide?.guide_id]);

    const handleDateSelect = (date: string) => {
        setSelectedDate(date);
        setSelectedTime('');
        setSelectedStartUtc('');
        setBookingError('');
    };

    const handleTimeSelect = (time: string, slotStartUtc?: string) => {
        setSelectedTime(time);
        setSelectedStartUtc(slotStartUtc || '');
        setBookingError('');

        // Client-side validation: verify the slot is actually available
        const selectedDay = availability.find((day) => day.date === selectedDate);
        const selectedSlot = selectedDay?.timeSlots.find((slot) => slot.time === time);

        if (!selectedSlot?.available) {
            setBookingError('This time slot is no longer available. Please select a different time.');
            return;
        }

        // Check if the time is in the past or within 4 hours
        // time is UTC (from guide-availability), so use setUTCHours
        const [hours, minutes] = time.split(':').map(Number);
        const slotDateTime = new Date(selectedDate + 'T00:00:00Z');
        slotDateTime.setUTCHours(hours, minutes, 0, 0);
        const now = new Date();
        const fourHoursFromNow = new Date(now.getTime() + 4 * 60 * 60 * 1000);

        if (slotDateTime <= fourHoursFromNow) {
            setBookingError('Sessions must be scheduled at least 4 hours in advance. Please select a later time.');
            return;
        }
    };

    const handleConfirmBooking = () => {
        if (!selectedTime || !selectedDate) return;

        // Client-side validation: verify the slot is still available
        const selectedDay = availability.find((day) => day.date === selectedDate);
        const selectedSlot = selectedDay?.timeSlots.find((slot) => slot.time === selectedTime);

        if (!selectedSlot?.available) {
            setBookingError('This time slot is no longer available. Please select a different time.');
            return;
        }

        if (accountAlreadyCreated) {
            handleBookingWithPassword(undefined, selectedTime, true);
        } else if (providedPassword) {
            handleBookingWithPassword(providedPassword, selectedTime);
        } else {
            setShowPasswordPrompt(true);
        }
    };

    const handleBookingWithPassword = async (
        passwordToUse?: string,
        timeToUse?: string,
        skipAccountCreation = false
    ) => {
        const finalTime = timeToUse || selectedTime;
        const finalPassword = passwordToUse || password;

        if (!skipAccountCreation) {
            if (!finalPassword || finalPassword.length < 6) {
                setBookingError('Password must be at least 6 characters long');
                return;
            }
        }

        if (!selectedDate || !guide?.guide_id || !finalTime) return;

        // Double-check that the slot is still available before proceeding
        const selectedDay = availability.find((day) => day.date === selectedDate);
        const selectedSlot = selectedDay?.timeSlots.find((slot) => slot.time === finalTime);

        if (!selectedSlot?.available) {
            setBookingError('This time slot is no longer available. Please refresh and select a different time.');
            setShowPasswordPrompt(false);
            return;
        }

        // Double-check time is not in the past or within 4 hours
        // finalTime is UTC (from guide-availability), so use setUTCHours
        const [hours, minutes] = finalTime.split(':').map(Number);
        const slotDateTime = new Date(selectedDate + 'T00:00:00Z');
        slotDateTime.setUTCHours(hours, minutes, 0, 0);
        const now = new Date();
        const fourHoursFromNow = new Date(now.getTime() + 4 * 60 * 60 * 1000);

        if (slotDateTime <= fourHoursFromNow) {
            setBookingError('Sessions must be scheduled at least 4 hours in advance. Please select a later time.');
            setShowPasswordPrompt(false);
            return;
        }

        setIsBooking(true);
        setBookingError('');

        try {
            let authUserId: string | undefined = undefined;
            if (!skipAccountCreation) {
                const seekerId = AccountService.getSeekerIdFromStorage();
                if (!seekerId) {
                    throw new Error('No seeker ID found. Please complete onboarding first.');
                }

                const accountResult = await AccountService.createAccountForBooking(seekerId, finalPassword!);
                authUserId = accountResult.user_id;

                // Seeker record and profile are now created by the edge function
            } else {
                const { data: currentUser } = await supabase.auth.getUser();
                authUserId = currentUser?.user?.id ?? undefined;
            }

            // Get seeker info from local storage
            const basicInfoRaw = localStorage.getItem('basic_info_data');
            const basicInfo = basicInfoRaw ? JSON.parse(basicInfoRaw) : {};
            const seekerName = basicInfo?.seeker_full_name ?? '';
            const seekerEmail = basicInfo?.seeker_email ?? '';

            // Use the new session-schedule Edge Function
            const capiEventId = trackCapiEvent('Schedule');
            const { data, error } = await supabase.functions.invoke('session-schedule', {
                body: {
                    guide_id: guide.guide_id,
                    seeker_id: authUserId,
                    session_type: 'consultation',
                    session_date: selectedDate,
                    session_time: finalTime,
                    start_utc: selectedStartUtc || undefined,
                    duration_minutes: 20,
                    seeker_name: seekerName || null,
                    seeker_email: seekerEmail || null,
                    notes: '',
                    location: 'online',
                    seeker_timezone: seekerTimezone || undefined,
                    capi_event_id: capiEventId,
                },
            });

            if (error) {
                throw new Error(error.message || 'Failed to book consultation');
            }

            console.log('Consultation booked:', data);

            // Transform to ScheduledSession format for backward compatibility
            const session: ScheduledSession = {
                id: data.session_id,
                guide_id: guide.guide_id,
                seeker_id: authUserId!,
                session_type: 'consultation',
                session_date: selectedDate,
                session_time: finalTime,
                duration_minutes: 30,
                session_status: 'scheduled',
                seeker_name: seekerName || null,
                seeker_email: seekerEmail || null,
                notes: '',
                guide_name: guide.name || null,
                location: 'online',
                location_details: null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                // Include timezone-aware fields from the response
                start_time: data.start_time || null,
                end_time: data.end_time || null,
                guide_timezone: data.guide_timezone || null,
                seeker_timezone: data.seeker_timezone || null,
            };

            setShowPasswordPrompt(false);
            setPassword('');
            setSelectedTime('');

            if (onBookingComplete) {
                onBookingComplete(session);
            }
        } catch (error) {
            console.error('Booking failed:', error);
            setBookingError(error instanceof Error ? error.message : 'Failed to book consultation');
        } finally {
            setIsBooking(false);
        }
    };

    const guideName = guide?.name || 'Guide';
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date();
    const [viewingMonth, setViewingMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
    const consultationDuration = 20; // Consultation sessions are 20 minutes

    const firstDay = new Date(viewingMonth.getFullYear(), viewingMonth.getMonth(), 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const canGoBack = viewingMonth.getFullYear() > today.getFullYear() ||
        (viewingMonth.getFullYear() === today.getFullYear() && viewingMonth.getMonth() > today.getMonth());

    const handlePrevMonth = () => {
        if (canGoBack) {
            setViewingMonth(new Date(viewingMonth.getFullYear(), viewingMonth.getMonth() - 1, 1));
        }
    };

    const handleNextMonth = () => {
        setViewingMonth(new Date(viewingMonth.getFullYear(), viewingMonth.getMonth() + 1, 1));
    };

    // Helper to check if a date/time is in the past or within 4 hours from now
    // timeStr is UTC (from guide-availability), so use setUTCHours
    const isTimeTooSoon = (dateStr: string, timeStr: string): boolean => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const slotDateTime = new Date(dateStr + 'T00:00:00Z');
        slotDateTime.setUTCHours(hours, minutes, 0, 0);

        const now = new Date();
        const fourHoursFromNow = new Date(now.getTime() + 4 * 60 * 60 * 1000);

        return slotDateTime <= fourHoursFromNow;
    };

    const calendarCells = () => {
        return Array.from({ length: 42 }).map((_, index) => {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + index);
            const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
            const dayAvailability = availability.find((day) => day.date === dateKey);
            // Check if there are any slots that can fit the full consultation duration AND are not too soon
            const hasAvailability = dayAvailability?.timeSlots.some((slot) =>
                canFitSession(dayAvailability.timeSlots, slot.time, consultationDuration) &&
                !isTimeTooSoon(dateKey, slot.time)
            ) ?? false;
            const isCurrentMonth = currentDate.getMonth() === viewingMonth.getMonth() && currentDate.getFullYear() === viewingMonth.getFullYear();
            const isSelected = selectedDate === dateKey;

            const baseClasses =
                'h-12 rounded-2xl border text-sm font-semibold transition disabled:cursor-not-allowed';
            const stateClasses = isSelected
                ? 'border-[#304031] bg-[#304031] text-[#FFFCEF]'
                : hasAvailability && isCurrentMonth
                ? 'border-[#304031]/20 bg-[#FFFCEF] text-[#304031] hover:border-[#304031]'
                : 'border-transparent bg-transparent text-[#C7C1AD]';

            return (
                <button
                    key={dateKey}
                    onClick={() => handleDateSelect(dateKey)}
                    disabled={!hasAvailability || !isCurrentMonth}
                    className={`${baseClasses} ${stateClasses}`}
                >
                    {currentDate.getDate()}
                </button>
            );
        });
    };

    return (
        <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
            <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
                {/* Mobile header */}
                <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
                    <button
                        type="button"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
                        onClick={onBack}
                        aria-label="Go back"
                    >
                        {'\u2190'}
                    </button>
                    <div className="flex flex-1 justify-center">
                        <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
                    </div>
                    <div className="w-10" aria-hidden />
                </header>

                {/* Desktop header */}
                <div className="hidden lg:block">
                    <SeekerHeader />
                </div>

                {/* Desktop back button */}
                <div className="hidden lg:flex px-[120px] pt-8">
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 text-[#304031] hover:text-[#214D3C] transition-colors"
                        onClick={onBack}
                        aria-label="Go back"
                    >
                        <span className="text-xl">{'\u2190'}</span>
                        <span className="text-base font-medium">Back</span>
                    </button>
                </div>

                <main className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto lg:overflow-visible px-6 py-6 lg:px-[120px] lg:py-8">
                    <div className="rounded-[32px] border border-[#304031]/20 bg-[#FFF8E1]/90 p-6 shadow-[0_32px_60px_-35px_rgba(32,77,60,0.45)] lg:max-w-[480px]">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">
                            Booking with
                        </p>
                        <div className="mt-3 flex items-center gap-4">
                            {(guide as any)?.image_url || (guide as any)?.image ? (
                                <img
                                    src={(guide as any).image_url || (guide as any).image}
                                    alt={guideName}
                                    className="h-14 w-14 rounded-full object-cover border border-[#304031]/15"
                                />
                            ) : (
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#304031]/10">
                                    <svg className="h-7 w-7 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                            )}
                            <div>
                                <h1 className="font-recoleta text-[28px] lg:text-[36px] font-normal leading-snug text-[#304031] ph-mask">
                                    {guideName}
                                </h1>
                                {guide?.location && (
                                    <p className="text-sm text-[#214D3C]/70">{guide.location}</p>
                                )}
                            </div>
                        </div>
                        <p className="mt-3 text-sm lg:text-base text-[#214D3C]">
                            Select a date and time that works best for you. We'll confirm instantly.
                        </p>
                    </div>

                    {isLoadingAvailability && (
                        <div className="rounded-2xl border border-[#304031]/15 bg-white/70 p-5">
                            <div className="h-4 w-2/3 animate-pulse rounded-full bg-[#E8DFBD]" />
                            <div className="mt-4 h-32 animate-pulse rounded-2xl bg-[#E8DFBD]" />
                        </div>
                    )}

                    {bookingError && !isLoadingAvailability && (
                        <div className="rounded-2xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-4 py-3 text-sm font-medium text-[#B24E4E]">
                            {bookingError}
                        </div>
                    )}

                    {!isLoadingAvailability && availability.length > 0 && (
                        <div className="flex flex-col lg:flex-row gap-6">
                            {/* Calendar - Left Side */}
                            <div className="rounded-2xl border border-[#304031]/15 bg-white/85 p-5 lg:w-1/2">
                                <div className="flex items-center justify-between text-[#304031]">
                                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">
                                        Select a date
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={handlePrevMonth}
                                            disabled={!canGoBack}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#304031]/20 bg-white text-sm text-[#304031] transition hover:border-[#304031] disabled:cursor-not-allowed disabled:opacity-30"
                                            aria-label="Previous month"
                                        >
                                            {'\u2190'}
                                        </button>
                                        <span className="min-w-[140px] text-center text-sm font-medium text-[#214D3C]">
                                            {viewingMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={handleNextMonth}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#304031]/20 bg-white text-sm text-[#304031] transition hover:border-[#304031]"
                                            aria-label="Next month"
                                        >
                                            {'\u2192'}
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-4 grid grid-cols-7 gap-2 text-center text-xs font-semibold text-[#214D3C]/70">
                                    {dayLabels.map((label) => (
                                        <div key={label}>{label}</div>
                                    ))}
                                </div>
                                <div className="mt-2 grid grid-cols-7 gap-2">{calendarCells()}</div>
                            </div>

                            {/* Available Times - Right Side */}
                            {selectedDate && (
                                <div className="rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-5 lg:w-1/2">
                                    <div className="mb-2">
                                        <p className="text-base font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">
                                            Available times
                                        </p>
                                        {seekerTimezone && (
                                            <p className="mt-1 text-base text-[#214D3C]/70">
                                                Showing times in {formatTimezoneForDisplay(seekerTimezone)} timezone{seekerPostcode ? `, based on zip code ${seekerPostcode}` : ''}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mt-4 grid grid-cols-2 gap-3">
                                        {(() => {
                                            const daySlots = availability.find((day) => day.date === selectedDate)?.timeSlots ?? [];
                                            // Filter slots to only show those that can fit the full consultation duration AND are not in the past/too soon
                                            return daySlots
                                                .map((slot) => ({
                                                    ...slot,
                                                    available: slot.available &&
                                                               canFitSession(daySlots, slot.time, consultationDuration) &&
                                                               !isTimeTooSoon(selectedDate, slot.time),
                                                }))
                                                .map((slot) => {
                                                    // Convert UTC time to seeker's local time for display
                                                    const seekerLocalTime = convertUtcToSeekerTime(slot.time, selectedDate, seekerTimezone);
                                                    return (
                                                        <button
                                                            key={slot.time}
                                                            onClick={() => slot.available && handleTimeSelect(slot.time, (slot as any).start_utc)}
                                                            disabled={!slot.available}
                                                            className={[
                                                                'rounded-full border px-4 py-3 text-base font-semibold transition',
                                                                !slot.available
                                                                    ? 'border-[#C7C1AD]/30 bg-[#F5F5F5] text-[#C7C1AD] cursor-not-allowed line-through'
                                                                    : selectedTime === slot.time
                                                                    ? 'border-[#304031] bg-[#304031] text-[#FFFCEF]'
                                                                    : 'border-[#304031]/20 bg-white text-[#304031] hover:border-[#304031]',
                                                            ].join(' ')}
                                                        >
                                                            {formatTimeForDisplay(seekerLocalTime)}
                                                        </button>
                                                    );
                                                });
                                        })()}
                                    </div>

                                    {/* Confirm Booking Button */}
                                    {selectedTime && (
                                        <div className="mt-6 pt-6 border-t border-[#304031]/10">
                                            <div className="rounded-xl bg-[#FFF8E1]/90 p-4 mb-4">
                                                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80 mb-1">
                                                    Selected
                                                </p>
                                                <p className="text-lg font-semibold text-[#304031]">
                                                    {(() => {
                                                        const [year, month, day] = selectedDate.split('-').map(Number);
                                                        return new Date(year, month - 1, day).toLocaleDateString('en-US', {
                                                            weekday: 'long',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        });
                                                    })()}
                                                </p>
                                                <p className="text-base text-[#214D3C]">
                                                    at {(() => {
                                                        const seekerLocalTime = convertUtcToSeekerTime(selectedTime, selectedDate, seekerTimezone);
                                                        return formatTimeForDisplay(seekerLocalTime);
                                                    })()} ({consultationDuration} minutes)
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleConfirmBooking}
                                                disabled={isBooking}
                                                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031] bg-[#304031] px-6 py-4 text-base font-semibold text-[#FFFCEF] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.45)] transition duration-200 hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50"
                                            >
                                                {isBooking ? (
                                                    <>
                                                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#FFFCEF] border-t-transparent"></div>
                                                        Booking...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg
                                                            className="h-5 w-5"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                            strokeWidth={2}
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Confirm Booking
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {showPasswordPrompt && !accountAlreadyCreated && (
                        <div className="rounded-2xl border border-[#304031]/20 bg-white/95 p-5 shadow-[0_32px_60px_-35px_rgba(32,77,60,0.45)]">
                            <h3 className="text-lg font-semibold text-[#304031]">Create your account</h3>
                            <p className="mt-2 text-sm text-[#214D3C]">
                                Confirm your booking for {(() => {
                                    const [year, month, day] = selectedDate.split('-').map(Number);
                                    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric'
                                    });
                                })()} at {(() => {
                                    const seekerLocalTime = convertUtcToSeekerTime(selectedTime, selectedDate, seekerTimezone);
                                    return formatTimeForDisplay(seekerLocalTime);
                                })()} by adding a password.
                            </p>
                            <label className="mt-4 text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/80">
                                Password (min 6 characters)
                            </label>
                            <PasswordInput
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="stage-input mt-2"
                                placeholder="Create password"
                            />
                            {bookingError && (
                                <p className="mt-2 text-sm font-medium text-[#B24E4E]">{bookingError}</p>
                            )}
                            <div className="mt-4 flex gap-3">
                                <button
                                    type="button"
                                    className="inline-flex w-full items-center justify-center rounded-full border border-[#304031]/30 bg-[#FFF8E1] px-6 py-4 text-sm font-semibold text-[#304031] transition hover:-translate-y-0.5 hover:border-[#304031]"
                                    onClick={() => {
                                        setShowPasswordPrompt(false);
                                        setPassword('');
                                        setBookingError('');
                                    }}
                                    disabled={isBooking}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex w-full items-center justify-center rounded-full border border-[#304031] bg-[#304031] px-6 py-4 text-sm font-semibold text-[#FFFCEF] transition hover:-translate-y-0.5"
                                    onClick={() => handleBookingWithPassword(undefined, selectedTime)}
                                    disabled={isBooking || !password}
                                >
                                    {isBooking ? 'Booking...' : 'Book session'}
                                </button>
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {/* Site-wide footer - only visible on large screens */}
            <div className="hidden lg:block">
                <Footer />
            </div>
        </div>
    );
};
